import React, {FC, useMemo, useState} from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme
} from "@mui/material";

import {DtxSpacer} from "../../../../components/common/DtxSpacer";
import {DefaultButton} from "../../../../components/common/DefaultButton";
import {dataGridRowBorder} from "../../../../theme";
import {DialogHeading} from "../../AddAndUpdateSaas/dialogs/misc/styles";
import {LoadingSpinner} from "../../../../components/common/LoadingSpinner";
import {ICompany} from "../../../../types/Company";
import {ISaasClientRestriction} from "../../../../services/SaasRestrictions/SaasRestrictionsQueries";

export const availability = {
  allClients: 'all',
  selectedClients: 'selected',
}

export const atLeastOneClientMessage = 'Select at least one client';

// Mounted when opened, so it starts from the integration's current restriction
type propTypes = {
  integration: ISaasClientRestriction;
  clients: ICompany[];
  isOpen: boolean;
  isSpinning: boolean;
  onCancel: () => void;
  onConfirm: (restriction: {
    saas: string,
    restricted: boolean,
    clientIds: string[],
  }) => void;
};

// Lets the super admin choose whether a SaaS integration is
// available to all clients or only to selected ones (a custom integration)
export const EditIntegrationAvailabilityDialog: FC<propTypes> = ({
  integration,
  clients,
  isOpen,
  isSpinning,
  onCancel,
  onConfirm,
  ...props
}) => {
  const theme = useTheme();

  const [availableTo, setAvailableTo] = useState(
    integration.restricted ? availability.selectedClients : availability.allClients
  );
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>(
    integration.restricted ? integration.clientIds : []
  );
  const [didFindValidationErrorOnSubmit, setDidFindValidationErrorOnSubmit] = useState(false);

  // Every known client, plus any restricted-to client missing from the list
  // so saving does not silently drop it
  const clientOptions = useMemo(() => {
    const unknownClients = selectedClientIds
      .filter((id) => !clients.find((c) => c.id === id))
      .map((id) => ({id, name: id, slug: '', disabled: false}));
    return [...clients, ...unknownClients];
  }, [clients, selectedClientIds]);

  const selectedClients = clientOptions.filter((c) => selectedClientIds.includes(c.id));

  const isRestricted = availableTo === availability.selectedClients;
  const hasNoClients = isRestricted && selectedClientIds.length === 0;

  const handleSaveButtonClick = (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (hasNoClients) {
      setDidFindValidationErrorOnSubmit(true);
      return;
    }
    onConfirm({
      saas: integration.identifier,
      restricted: isRestricted,
      clientIds: isRestricted ? selectedClientIds : [],
    });
  }

  return (
    <Dialog
      {...props}
      open={isOpen}
    >
      <Box
        sx={{
          border: dataGridRowBorder,
          borderRadius: theme.more.borderRadius,
          margin: 'auto',
          padding: theme.spacing(2),
          minWidth: '600px',
          maxWidth: '600px',
        }}
      >
        <DialogHeading>
          Update integration
        </DialogHeading>
        {isSpinning ?
          <LoadingSpinner></LoadingSpinner> :
          <form onSubmit={handleSaveButtonClick}>
            <DtxSpacer space={2} />
            <InputLabel>Name</InputLabel>
            <Typography sx={{fontSize: theme.typography.h5.fontSize}}>
              {integration.name}
            </Typography>
            <DtxSpacer space={2} />
            <InputLabel id="integration-available-to">Available to</InputLabel>
            <FormControl>
              <RadioGroup
                name="availableTo"
                aria-labelledby="integration-available-to"
                value={availableTo}
                onChange={(event) => {
                  setAvailableTo(event.target.value);
                  setDidFindValidationErrorOnSubmit(false);
                }}
              >
                <FormControlLabel
                  value={availability.allClients}
                  control={<Radio />}
                  label="All clients" />
                <FormControlLabel
                  value={availability.selectedClients}
                  control={<Radio />}
                  label="Selected clients (custom integration)" />
              </RadioGroup>
            </FormControl>
            {isRestricted && <>
              <DtxSpacer space={1} />
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={clientOptions}
                value={selectedClients}
                getOptionLabel={(client) => client.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_event, value) => {
                  setSelectedClientIds(value.map((c) => c.id));
                  setDidFindValidationErrorOnSubmit(false);
                }}
                renderOption={(optionProps, client, {selected}) => {
                  const {key, ...otherOptionProps} = optionProps as
                    React.HTMLAttributes<HTMLLIElement> & {key: React.Key};
                  return (
                    <li key={key} {...otherOptionProps}>
                      <Checkbox size="small" checked={selected} sx={{marginRight: theme.spacing(1)}} />
                      {client.disabled ? `${client.name} (disabled)` : client.name}
                    </li>
                  );
                }}
                renderInput={(params) =>
                  <TextField
                    {...params}
                    label="Clients"
                    placeholder={selectedClients.length ? '' : 'Choose clients'}
                    error={didFindValidationErrorOnSubmit && hasNoClients}
                  />}
              />
              {didFindValidationErrorOnSubmit && hasNoClients &&
                <FormHelperText error>{atLeastOneClientMessage}</FormHelperText>}
              <DtxSpacer space={1} />
              <Typography variant="body2" color="text.secondary">
                Only users of the selected clients see this integration when adding a SaaS.
              </Typography>
            </>}
            <DtxSpacer space={4} />
            <Box sx={{textAlign: 'right'}}>
              <DefaultButton
                sx={{marginRight: theme.spacing()}}
                variant="contained"
                onClick={onCancel}
              >
                Cancel
              </DefaultButton>
              <DefaultButton
                sx={{marginRight: 0}}
                variant="contained"
                type="submit"
              >
                Save
              </DefaultButton>
            </Box>
          </form>
        }
      </Box>
    </Dialog>
  );
};
